import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { ArrowRightIcon, HandCoinsIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';

export type FinancingHeroProps = {
  /** Click handler on the primary CTA. */
  onApply: () => void;
  /**
   * Compact variant — drops the illustration block and the trust
   * line, halves the vertical padding. Use it as a page header on
   * the carrier dashboard view where the hero shares the viewport
   * with the applications table.
   */
  variant?: 'full' | 'compact';
  /**
   * Hide the primary "Apply now" button (keeps eyebrow, headline,
   * subhead, and illustration). Use when an upstream feature flag
   * disables new applications but the marketing surface should
   * still render.
   */
  hideCta?: boolean;
  className?: string;
};

/**
 * Welcoming gradient hero shared between the carrier `/financing`
 * dashboard and any future marketing landing page. Reads its copy
 * from the `m-financing` namespace so both contexts can localise.
 *
 * Two variants:
 *  - `full` (default): hero with eyebrow, headline, subhead, CTA,
 *    trust line, and the three-card "invoice → offer → funded"
 *    illustration block. Use for marketing landing pages and the
 *    "About financing" tab.
 *  - `compact`: same gradient + eyebrow + headline + CTA, no
 *    illustration. Use as a page header where the user is already
 *    convinced and just needs the entry point.
 */
export function FinancingHero({
  onApply,
  variant = 'full',
  hideCta = false,
  className,
}: FinancingHeroProps) {
  const { t } = useTranslation('m-financing');
  const isCompact = variant === 'compact';

  return (
    <>
      <style>{`
        @keyframes financing-hero-enter {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          60% { opacity: 1; transform: translateY(-3px) scale(1.005); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes financing-hero-gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes financing-hero-orb {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-10px, 8px) scale(1.06); }
        }
        @keyframes financing-hero-orb-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(12px, -6px) scale(1.04); }
        }
      `}</style>

      <section
        className={cn(
          'relative overflow-hidden rounded-2xl border border-emerald-200/60',
          isCompact ? 'p-5 sm:p-6' : 'p-6 sm:p-10',
          className,
        )}
        style={{
          background:
            'linear-gradient(135deg, #ECFDF5, #D1FAE5, #A7F3D0, #D1FAE5, #ECFDF5)',
          backgroundSize: '300% 300%',
          animation:
            'financing-hero-enter 0.7s cubic-bezier(0.22, 1, 0.36, 1) both, financing-hero-gradient 12s ease-in-out infinite',
        }}>
        {/* Decorative floating orbs */}
        <div
          className="pointer-events-none absolute -top-16 -end-16 size-56 rounded-full bg-emerald-300/20"
          style={{ animation: 'financing-hero-orb 9s ease-in-out infinite' }}
        />
        <div
          className="pointer-events-none absolute -bottom-12 -start-12 size-44 rounded-full bg-emerald-400/15"
          style={{ animation: 'financing-hero-orb-2 11s ease-in-out infinite' }}
        />
        {!isCompact && (
          <div className="pointer-events-none absolute top-1/3 end-1/4 size-20 rounded-full bg-emerald-200/40" />
        )}

        <div
          className={cn(
            'relative flex flex-col items-start gap-6 sm:flex-row sm:items-center',
            isCompact ? '' : 'sm:gap-10',
          )}>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/60 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800 backdrop-blur-sm">
              <HandCoinsIcon className="size-4" />
              {t('landing.hero.eyebrow')}
            </div>
            <h1
              className={cn(
                'mt-4 font-bold leading-tight text-emerald-950',
                isCompact ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl',
              )}>
              {t('landing.hero.headline')}
            </h1>
            {!isCompact && (
              <p className="mt-3 max-w-xl text-base text-emerald-900/80 sm:text-lg">
                {t('landing.hero.subhead')}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {!hideCta && (
                <Button
                  type="button"
                  onClick={onApply}
                  className="bg-emerald-700 text-white shadow-md hover:bg-emerald-800">
                  {t('landing.hero.cta_apply')}
                  <ArrowRightIcon className="ms-2 size-4 rtl:rotate-180" />
                </Button>
              )}
              {!isCompact && (
                <p className="text-xs text-emerald-900/70 sm:text-sm">
                  {t('landing.hero.trust_line')}
                </p>
              )}
            </div>
          </div>

          {/* Decorative illustration block — three stacked cards
              suggesting the "invoice -> offer -> cash" flow. Hidden
              entirely in `compact` variant and on mobile. */}
          {!isCompact && (
            <div className="relative hidden h-48 w-56 shrink-0 sm:block">
              <div
                className="absolute inset-x-4 top-0 h-28 rounded-2xl border border-white/70 bg-white/90 shadow-xl backdrop-blur"
                style={{ transform: 'rotate(-6deg)' }}>
                <div className="flex h-full flex-col justify-between p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                    {t('landing.hero.illustration.invoice_label')}
                  </div>
                  <div className="text-lg font-bold text-emerald-900">
                    {t('landing.hero.illustration.invoice_amount')}
                  </div>
                  <div className="h-1.5 w-3/4 rounded-full bg-emerald-100" />
                </div>
              </div>
              <div
                className="absolute inset-x-2 top-10 h-28 rounded-2xl border border-white/70 bg-white/90 shadow-xl backdrop-blur"
                style={{ transform: 'rotate(2deg)' }}>
                <div className="flex h-full flex-col justify-between p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                    {t('landing.hero.illustration.offer_label')}
                  </div>
                  <div className="text-lg font-bold text-emerald-900">
                    {t('landing.hero.illustration.offer_amount')}
                  </div>
                  <div className="flex gap-1">
                    <div className="h-1.5 flex-1 rounded-full bg-emerald-100" />
                    <div className="h-1.5 flex-1 rounded-full bg-emerald-200" />
                  </div>
                </div>
              </div>
              <div
                className="absolute inset-x-0 top-20 h-28 rounded-2xl border border-emerald-200 bg-emerald-700 shadow-2xl"
                style={{ transform: 'rotate(-2deg)' }}>
                <div className="flex h-full flex-col justify-between p-3 text-white">
                  <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                    {t('landing.hero.illustration.funded_label')}
                  </div>
                  <div className="text-lg font-bold">
                    {t('landing.hero.illustration.funded_amount')}
                  </div>
                  <div className="h-1.5 w-1/2 rounded-full bg-white/40" />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
